/**
 * Naming a code block when a student downloads it.
 *
 * Shared by the embeddable widget and the web chat so the same block saves under the
 * same name wherever it was shown. No prompt change is involved: the tutor writes code
 * blocks exactly as before, and the name is read off the block itself.
 */

/** Fence language (lowercased) → file extension. Unknown languages save as .txt. */
const EXTENSIONS: Record<string, string> = {
  r: "R",
  rmd: "Rmd",
  rmarkdown: "Rmd",
  python: "py",
  py: "py",
  java: "java",
  javascript: "js",
  js: "js",
  jsx: "jsx",
  typescript: "ts",
  ts: "ts",
  tsx: "tsx",
  c: "c",
  h: "h",
  cpp: "cpp",
  "c++": "cpp",
  csharp: "cs",
  cs: "cs",
  go: "go",
  rust: "rs",
  ruby: "rb",
  php: "php",
  kotlin: "kt",
  swift: "swift",
  scala: "scala",
  haskell: "hs",
  julia: "jl",
  lua: "lua",
  perl: "pl",
  matlab: "m",
  octave: "m",
  sql: "sql",
  html: "html",
  css: "css",
  scss: "scss",
  json: "json",
  yaml: "yaml",
  yml: "yaml",
  toml: "toml",
  ini: "ini",
  xml: "xml",
  csv: "csv",
  tsv: "tsv",
  markdown: "md",
  md: "md",
  latex: "tex",
  tex: "tex",
  bash: "sh",
  sh: "sh",
  shell: "sh",
  zsh: "sh",
  powershell: "ps1",
  ps1: "ps1",
  bat: "bat",
};

/** A bare filename, e.g. "analysis.R" or "Main.java" — no path, no spaces. */
const FILENAME = /^[A-Za-z0-9_][A-Za-z0-9_.-]*\.[A-Za-z][A-Za-z0-9]{0,7}$/;

/** Comment markers a first line might use to name its own file: "# analysis.R", "// Main.java". */
const COMMENT_PREFIX = /^\s*(?:#|\/\/|--|%|;|\/\*+|<!--)\s*/;
const COMMENT_SUFFIX = /\s*(?:\*\/|-->)?\s*$/;

export function extensionForLanguage(language: string | null | undefined): string {
  return EXTENSIONS[(language || "").trim().toLowerCase()] ?? "txt";
}

/**
 * The filename to offer for a code block.
 *
 * 1. A first line that is only a comment naming a file ("# clean_data.R") — tutors and
 *    textbooks often label code this way, and it is the name the student was told.
 * 2. Java: the public top-level type, since javac refuses a file whose name does not
 *    match it. Saving `public class Main` as snippet.java would hand a student a file
 *    that fails to compile for a reason that has nothing to do with their code.
 * 3. Otherwise "snippet.<ext>".
 */
export function suggestCodeFilename(language: string | null | undefined, code: string): string {
  const ext = extensionForLanguage(language);

  const firstLine = code.split("\n").find((l) => l.trim() !== "") ?? "";
  if (COMMENT_PREFIX.test(firstLine)) {
    const candidate = firstLine.replace(COMMENT_PREFIX, "").replace(COMMENT_SUFFIX, "");
    if (FILENAME.test(candidate)) return candidate;
  }

  if (ext === "java") {
    const m = code.match(
      /^\s*public\s+(?:(?:abstract|final|sealed|non-sealed|static|strictfp)\s+)*(?:class|interface|enum|record|@interface)\s+([A-Za-z_$][\w$]*)/m
    );
    if (m) return `${m[1]}.java`;
  }

  return `snippet.${ext}`;
}
